import { useMemo } from 'react';

interface CalendarDay {
    date: Date;
    day: number;
    isCurrentMonth: boolean;
    isToday: boolean;
    isSelected: boolean;
    isDisabled: boolean;
}

export function useCalendarDays(
    currentMonth: Date,
    currentYear: Date,
    selectedDate: Nullable<Date>,
    minDate?: Date,
    maxDate?: Date,
): CalendarDay[] {
    return useMemo(() => {
        const year = currentYear.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const firstDayOfWeek = firstDay.getDay();
        const daysFromPrevMonth = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

        const days: CalendarDay[] = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const normalizedMinDate = minDate ? new Date(minDate.getTime()) : undefined;
        if (normalizedMinDate) normalizedMinDate.setHours(0, 0, 0, 0);

        const normalizedMaxDate = maxDate ? new Date(maxDate.getTime()) : undefined;
        if (normalizedMaxDate) normalizedMaxDate.setHours(0, 0, 0, 0);

        const prevMonthLastDay = new Date(year, month, 0).getDate();
        for (let i = prevMonthLastDay - daysFromPrevMonth + 1; i <= prevMonthLastDay; i++) {
            const date = new Date(year, month - 1, i);
            days.push({
                date,
                day: i,
                isCurrentMonth: false,
                isToday: false,
                isSelected: false,
                isDisabled: false,
            });
        }

        for (let i = 1; i <= lastDay.getDate(); i++) {
            const date = new Date(year, month, i);
            let isDisabled = false;
            if (normalizedMinDate && date < normalizedMinDate) isDisabled = true;
            if (normalizedMaxDate && date > normalizedMaxDate) isDisabled = true;

            days.push({
                date,
                day: i,
                isCurrentMonth: true,
                isToday: date.getTime() === today.getTime(),
                isSelected: selectedDate
                    ? date.getTime() === new Date(selectedDate.getTime()).setHours(0, 0, 0, 0)
                    : false,
                isDisabled,
            });
        }

        const totalDaysToShow = 42;
        const remainingDays = totalDaysToShow - days.length;
        for (let i = 1; i <= remainingDays; i++) {
            days.push({
                date: new Date(year, month + 1, i),
                day: i,
                isCurrentMonth: false,
                isToday: false,
                isSelected: false,
                isDisabled: false,
            });
        }

        return days;
    }, [currentYear, currentMonth, minDate, maxDate, selectedDate]);
}
